import { NpcLink } from "~/components/npc/NpcLink";
import { gameName } from "./common";
import { GameVer, lstring, Realm, tt } from "./Lang";
import { getGenSkillsGameVer } from "./skill";
import { StrongId } from "~/types";
import { OurLoaderArgs } from "./Loaders";
import { pg } from "~/pg.server";
import { and, eq } from "drizzle-orm";
import { loadMarkdown } from "./Markdown.server";

export function getDescription(props: {
  realm: Realm;
  npc: {
    name: lstring;
    nick: lstring;
    level: number | null;
    race: { name: string };
    orgHp: number | null;
    orgMp: number | null;
    exp: number | null;
    sp: number | null;
    type: string;
  };
  locs: string;
}) {
  const { realm, npc } = props;
  const t = tt.bind(null, realm.lang);

  const level = npc.level;
  let nameRu = npc.name.ru;
  let nameEn = npc.name.en;
  if (npc.nick != null) {
    nameRu = `${npc.nick.ru} ${npc.name.ru}`.trim();
    nameEn = `${npc.nick.en} ${npc.name.en}`.trim();
  }
  const citizens = new Set([
    "teleporter",
    "citizen",
    "guild_coach",
    "guild_master",
    "guard",
    "merchant",
    "package_keeper",
    "warehouse_keeper",
  ]);
  const game = gameName(realm.game);

  const npcReward = !citizens.has(props.npc.type)
    ? ` За убийство ${nameRu} дают ${Math.round(
        props.npc.exp ?? 0
      )} очков опыта и ${Math.round(props.npc.sp ?? 0)} очков умений.`
    : "";

  return t({
    en: `${nameEn} is a level ${level} NPC. Race ${npc.race?.name}.${
      props.locs ? ` Locations: ${props.locs} (View on the map).` : ""
    } ${game}.`.trim(),
    ru: `НПЦ ${nameRu} (${nameEn}) - ${
      npc.race?.name
    } ${level} уровня, здоровье: ${Math.round(
      props.npc.orgHp ?? 0
    )} и мана: ${Math.round(props.npc.orgMp ?? 0)}.${npcReward}${
      props.locs ? ` Можно найти в ${props.locs} (Посмотреть на карте).` : ""
    } ${game}.`.trim(),
  });
}

export function getLocs(
  locs: {
    id: number;
    name: string;
  }[],
  realm: Realm
) {
  const t = tt.bind(null, realm.lang);
  const locsMap = new Map<number, string>();

  for (const loc of locs) {
    locsMap.set(loc.id, loc.name);
  }

  return Array.from(locsMap.values()).join(", ");
}

export function getNpcList(
  npcs: {
    type?: string | null;
    name: string;
    nick: string | null;
    nickColor: "default" | "quest" | "raid";
    npcName: string;
    level: number | null;
  }[]
) {
  return npcs.map((npc, i) => (
    <span key={i}>
      <NpcLink npc={npc} />
      {i < npcs.length - 1 ? ", " : ""}
    </span>
  ));
}

export function getStrengths(
  skills: {
    id: number;
    skillName: string;
    name: string;
    icon: string;
  }[]
) {
  const map = new Map();

  const strengths = [
    "s_npc_resist",
    "s_npc_prop_md_lv_p",
    "s_npc_prop_pd_lv_p",
    "s_npc_prop_ma_lv_p",
    "s_npc_prop_pa_lv_p",
  ];

  for (const str of strengths) {
    for (const s of skills) {
      if (s.skillName.includes(str)) {
        map.set(s.id, s);
      }
    }
  }

  return Array.from(map.values());
}

export function getWeakness(
  skills: {
    id: number;
    skillName: string;
    name: string;
    icon: string;
  }[]
) {
  const map = new Map();

  const weakness = [
    "s_npc_weakness",
    "s_npc_prop_md_lv_n",
    "s_npc_prop_pd_lv_n",
    "s_npc_prop_ma_lv_n",
    "s_npc_prop_pa_lv_n",
  ];

  for (const w of weakness) {
    for (const s of skills) {
      if (s.skillName.includes(w)) {
        map.set(s.id, s);
      }
    }
  }

  return Array.from(map.values());
}

export function getHpRate(
  skills: {
    id: number;
    skillName: string;
    name: string;
    icon: string;
  }[],
  gver: GameVer
) {
  if (getGenSkillsGameVer(gver) === "OLD") {
    for (const sid of Object.values(StrongId.Values)) {
      for (const s of skills) {
        if (s.id === parseInt(sid)) {
          return s;
        }
      }
    }
  } else {
    for (const s of skills) {
      if (s.id === 4408) {
        return s;
      }
    }
  }
}

export async function npcMdPage(cx: OurLoaderArgs, npcName: string) {
  const page = await pg.db
    .select({
      contentEn: pg.mdRevisions.contentEn,
      contentRu: pg.mdRevisions.contentRu,
    })
    .from(pg.mdPages)
    .where(
      and(eq(pg.mdPages.npcName, npcName), eq(pg.mdPages.isDeleted, false))
    )
    .innerJoin(pg.mdRevisions, eq(pg.mdPages.revisionId, pg.mdRevisions.id))
    .then((res) => res.at(0));

  return {
    content:
      page?.contentEn && cx.realm.lang === "en"
        ? await loadMarkdown(
            page?.contentEn,
            cx.gd.all_gd!,
            cx.realm.lang,
            cx.realm.game
          )
        : page?.contentRu &&
          (await loadMarkdown(
            page?.contentRu,
            cx.gd.all_gd!,
            cx.realm.lang,
            cx.realm.game
          )),
  };
}
